import React, { FC, useEffect, useState } from 'react';
import { FileMeta } from '../../../store/mediaAPI/document/dto/request/document-save-request';
import LandscapeProjectsAddProjectsDocumentsTemplate from '../../templates/LandscapeProjectsAddProjectsDocumentsTemplate';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store/store';
import { useParams } from 'react-router-dom';
import Spinner from '../../atoms/Spinner/Spinner';
import { LandscapeDocumentResponseDto } from '../../../store/landscapeAPI/landscape/dto/response/landscape-document-response.dto';
import {
  deleteLandscapeDocument,
  fetchLandscapeDocumentById,
  fetchLandscapeDocumentsByProjectId,
  saveLandscapeDocument,
  updateLandscapeDocument,
} from '../../../store/landscapeAPI/landscape/reducers/landscape-document.slice';
import { DocumentSaveResponseDto } from '../../../store/mediaAPI/document/dto/response/document-save-response';
import { saveDocument } from '../../../store/mediaAPI/document/reducers/document.slice';
import { LandscapeDocumentSaveRequestDto } from '../../../store/landscapeAPI/landscape/dto/request/landscape-document-save-request.dto';
import { LandscapeDocumentEntityType } from '../../../store/landscapeAPI/landscape/landcscape-document-entity-type';
import { getUUID } from '../../../utils/UUID';
import { LandscapeDocumentUpdateRequestDto } from '../../../store/landscapeAPI/landscape/dto/request/landscape-document-update-request.dto';
import { EditLandscapeDocumentModal } from '../../organisms';
import LandscapeProjectNotSavedInfoPage from '../LandscapeProjectNotSavedInfoPage';
import { LocalesLanguagesConstants } from '../../../locales/locales';

interface LandscapeProjectsAddProjectsDocumentsPageProps {}

const LandscapeProjectsAddProjectsDocumentsPage: FC<
  LandscapeProjectsAddProjectsDocumentsPageProps
> = () => {
  const dispatch = useDispatch<AppDispatch>();

  const { id, projectId } = useParams();

  const [file, setFile] = useState<string>();

  const [fileMeta, setFileMeta] = useState<FileMeta>();

  const [fileName, setFileName] = useState<string>();

  const [documentToEdit, setDocumentToEdit] =
    useState<LandscapeDocumentResponseDto>();

  const [showModal, setShowModal] = useState<boolean>(false);

  const handleOnChange = async (document: LandscapeDocumentResponseDto) => {
    setDocumentToEdit(document);
  };

  const { landscapeDocuments, status } = useSelector(
    (state: RootState) => state.landscapeDocumentSlice
  );

  const handleEditAction = async (id: string) => {
    const documentResponse = (
      await dispatch(
        fetchLandscapeDocumentById({
          id,
          language: LocalesLanguagesConstants.en,
        })
      )
    ).payload as LandscapeDocumentResponseDto;
    setDocumentToEdit(documentResponse);
    setShowModal(true);
  };

  const handleEdit = async (document: LandscapeDocumentResponseDto) => {
    const documentToUpdate: LandscapeDocumentUpdateRequestDto = {
      compactId: document.compactId,
      file: document.file,
      name: document.name,
      description: document.description,
      documentDate: document.documentDate,
      entityId: document.entityId,
      entityType: document.entityType,
      id: document.id,
      published: document.published,
    };
    await dispatch(
      updateLandscapeDocument({
        document: documentToUpdate,
        language: LocalesLanguagesConstants.en,
      })
    );
    if (projectId) {
      dispatch(fetchLandscapeDocumentsByProjectId(projectId));
    }
    setShowModal(false);
  };

  const handleDownload = async (documentId: string) => {
    const documentToDownload = (
      await dispatch(
        fetchLandscapeDocumentById({
          id: documentId,
          language: LocalesLanguagesConstants.en,
        })
      )
    ).payload as LandscapeDocumentResponseDto;

    const link = document.createElement('a');
    const fileUrl = documentToDownload.file;
    link.href = documentToDownload.file;
    link.download = documentToDownload.name;
    link.target = '_blank';

    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(fileUrl);
  };

  const handleDelete = async (documentId: string) => {
    if (projectId) {
      await dispatch(
        deleteLandscapeDocument({
          existingId: documentId,
          compactId: id!,
        })
      );
      dispatch(fetchLandscapeDocumentsByProjectId(projectId));
    }
  };

  const handleFileOnChange = (file: string) => {
    setFile(file);
  };

  const handleFileMetaOnChange = (fileMeta: FileMeta) => {
    setFileMeta(fileMeta);
  };

  const handleFileNameOnChange = (fileName: string) => {
    setFileName(fileName);
  };

  const callbackFn = async () => {
    let fileSaveResponse: DocumentSaveResponseDto = {
      href: '',
    };
    if (file && file !== '' && fileMeta) {
      const formData = new FormData();
      formData.append(
        'file',
        new Blob([file], {
          type: fileMeta?.fileType,
        }),
        fileMeta?.fileSrc
      );
      fileSaveResponse = (await dispatch(saveDocument(formData)))
        .payload as DocumentSaveResponseDto;
    }
    const landscapeDocumentToSave: LandscapeDocumentSaveRequestDto = {
      compactId: id!,
      file: fileSaveResponse.href,
      name: fileName ?? '',
      description: '',
      documentDate: '',
      entityId: projectId!,
      entityType: LandscapeDocumentEntityType.Project,
      id: getUUID(),
      published: true,
    };
    await dispatch(saveLandscapeDocument(landscapeDocumentToSave));
    await dispatch(fetchLandscapeDocumentsByProjectId(projectId!));
  };

  useEffect(() => {
    if (projectId && projectId !== 'new-project') {
      dispatch(fetchLandscapeDocumentsByProjectId(projectId));
    }
  }, [projectId, dispatch]);

  if (status === 'loading') {
    return <Spinner />;
  }

  return projectId && projectId !== 'new-project' ? (
    <div>
      {showModal && documentToEdit && (
        <EditLandscapeDocumentModal
          show={showModal}
          setShow={setShowModal}
          handleEdit={handleEdit}
          handleOnChange={handleOnChange}
          document={documentToEdit}
        />
      )}
      <LandscapeProjectsAddProjectsDocumentsTemplate
        data={landscapeDocuments ?? []}
        handleDownload={handleDownload}
        handleEditAction={handleEditAction}
        handleDelete={handleDelete}
        handleFileOnChange={handleFileOnChange}
        handleFileMetaOnChange={handleFileMetaOnChange}
        handleFileNameOnChange={handleFileNameOnChange}
        callbackFn={callbackFn}
      />
    </div>
  ) : (
    <LandscapeProjectNotSavedInfoPage />
  );
};

export default LandscapeProjectsAddProjectsDocumentsPage;
